import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LockSimple, ArrowLeft, Eye, FloppyDisk } from "@phosphor-icons/react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BRAND_META } from "../../context/BrandContext";
import { EMPTY_FIGURES, FORM_GROUPS } from "./formSpec";
import ProofUploader from "./ProofUploader";
import SimpleReportView from "./SimpleReportView";
import StatusBanner from "./StatusBanner";
import { apiError, btnGhost, btnPrimary, inp, todayWIB } from "./util";

export function NumberField({ k, label, type, value, onChange, error, hint }) {
    const shown = value ? Number(value).toLocaleString("id-ID") : "";
    return (
        <label className="block text-xs text-neutral-500">
            {label}
            {hint && <span className="block text-[11px] text-neutral-400">{hint}</span>}
            <div className="relative mt-1">
                {type === "money" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">Rp</span>}
                <input
                    inputMode="numeric"
                    value={shown}
                    placeholder="0"
                    onChange={(e) => onChange(k, parseInt(e.target.value.replace(/\D/g, "") || "0", 10))}
                    className={`${inp} tabular-nums ${type === "money" ? "pl-9" : ""} ${error ? "border-red-400" : ""}`}
                    data-testid={`field-${k}`}
                />
            </div>
            {error && <span className="text-red-600 text-[11px]" data-testid={`field-error-${k}`}>{error}</span>}
        </label>
    );
}

function FormBody({ figures, setField, fieldErrors, handover, setHandover }) {
    return (
        <>
            {FORM_GROUPS.map((g) => (
                <section key={g.title} className="bg-white/70 border border-mist rounded-sm p-4 md:p-5">
                    <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-3">{g.title}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {g.fields.map(([k, label, type, hint]) => (
                            <NumberField key={k} k={k} label={label} type={type} hint={hint} value={figures[k]} onChange={setField} error={fieldErrors[k]} />
                        ))}
                    </div>
                </section>
            ))}
            <section className="bg-white/70 border border-mist rounded-sm p-4 md:p-5 space-y-3">
                <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Serah terima</h3>
                <label className="block text-xs text-neutral-500">
                    Catatan customer yang masih tertunda
                    <textarea rows={3} value={handover.notes} onChange={(e) => setHandover({ ...handover, notes: e.target.value })} className={`${inp} mt-1`} data-testid="field-handover-notes" />
                </label>
                <label className="block text-xs text-neutral-500">
                    PIC yang ditugaskan (jika ada)
                    <input value={handover.pic} onChange={(e) => setHandover({ ...handover, pic: e.target.value })} className={`${inp} mt-1`} data-testid="field-handover-pic" />
                </label>
            </section>
        </>
    );
}

export default function DailyReportForm({ brand, onSubmitted }) {
    const { user } = useAuth();
    const [date, setDate] = useState(todayWIB());
    const [report, setReport] = useState(undefined);
    const [figures, setFigures] = useState(EMPTY_FIGURES);
    const [handover, setHandover] = useState({ notes: "", pic: "" });
    const [jubelio, setJubelio] = useState([]);
    const [other, setOther] = useState([]);
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setReport(undefined);
        setPreview(null);
        setErrors([]);
        try {
            const { data } = await api.get("/reporting/simple-reports/mine", { params: { brand, reporting_date: date } });
            const r = data.report;
            setReport(r);
            setFigures(r?.figures ? { ...EMPTY_FIGURES, ...r.figures } : EMPTY_FIGURES);
            setHandover(r?.handover || { notes: "", pic: "" });
            setJubelio(data.attachments.filter((a) => a.kind === "jubelio_summary"));
            setOther(data.attachments.filter((a) => a.kind !== "jubelio_summary"));
        } catch (e) {
            toast.error(await apiError(e));
            setReport(null);
        }
    }, [brand, date]);
    useEffect(() => {
        load();
    }, [load]);

    const status = report?.status || null;
    const isSubmitted = status === "SUBMITTED" || status === "REOPENED";
    const setField = (k, v) => setFigures((f) => ({ ...f, [k]: v }));
    const ids = [...jubelio, ...other].map((a) => a.id);
    const payload = () => ({ brand, reporting_date: date, figures, handover, attachment_ids: ids });
    const fieldErrors = Object.fromEntries(errors.filter((e) => e.field).map((e) => [e.field, e.message]));

    const run = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            if (okMsg) toast.success(okMsg);
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    const saveDraft = () => run(async () => { const { data } = await api.post("/reporting/simple-reports/draft", payload()); setReport(data); }, "Draf disimpan");
    const review = () => run(async () => {
        const { data } = await api.post("/reporting/simple-reports/preview", payload());
        setErrors(data.errors);
        if (data.ok) setPreview(data);
        else toast.error("Mohon perbaiki isian yang ditandai");
    });
    const submit = () => run(async () => {
        if (isSubmitted) await api.put(`/reporting/simple-reports/${report.id}`, { figures, handover, attachment_ids: ids });
        else await api.post("/reporting/simple-reports", payload());
        onSubmitted();
    }, isSubmitted ? "Koreksi disimpan — revisi baru tercatat" : "Laporan harian terkirim");

    const header = (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white/70 border border-mist rounded-sm p-4 text-sm" data-testid="report-header">
            <div><div className="text-[10px] uppercase tracking-widest text-neutral-400">Admin</div><div className="mt-1" data-testid="report-admin">{user?.name}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-neutral-400">Brand</div><div className="mt-1" data-testid="report-brand">{BRAND_META[brand]?.name || brand}</div></div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400">
                Tanggal Laporan
                <input type="date" value={date} max={todayWIB()} disabled={!!preview} onChange={(e) => setDate(e.target.value)} className={`${inp} mt-1 normal-case tracking-normal`} data-testid="simple-date-input" />
            </label>
        </div>
    );

    if (report === undefined) return <div className="space-y-5 max-w-3xl">{header}<div className="text-sm text-neutral-400">Memuat…</div></div>;
    if (report && report.method !== "SIMPLE_FORM") {
        return <div className="space-y-5 max-w-3xl">{header}<div className="border border-mist bg-white/70 rounded-sm p-4 text-sm" data-testid="excel-submitted-note">Tanggal ini sudah dikirim lewat Laporan Detail (Excel). Lihat di Riwayat Laporan.</div></div>;
    }
    if (status === "APPROVED") {
        return (
            <div className="space-y-5" data-testid="simple-approved">
                {header}
                <StatusBanner report={report} />
                <SimpleReportView figures={report.figures} handover={report.handover} totals={report.totals} attachments={[...jubelio, ...other]} />
            </div>
        );
    }
    if (preview) {
        return (
            <div className="space-y-6" data-testid="simple-preview">
                {header}
                <div className="border border-amber-200 bg-amber-50/60 rounded-sm p-3 text-xs text-amber-800">
                    {isSubmitted ? "Menyimpan akan membuat revisi baru. Angka dan bukti sebelumnya tetap tersimpan di riwayat." : "Periksa dulu sebelum mengirim. Laporan masih bisa dikoreksi sampai Owner menyetujui."}
                </div>
                <SimpleReportView figures={figures} handover={handover} totals={preview.totals} attachments={preview.attachments} />
                <div className="flex flex-wrap gap-3 justify-end sticky bottom-20 md:bottom-4">
                    <button type="button" onClick={() => setPreview(null)} className={btnGhost} data-testid="simple-back-btn"><ArrowLeft size={14} /> Ubah</button>
                    <button type="button" onClick={submit} disabled={busy} className={btnPrimary} data-testid="simple-submit-btn">
                        <LockSimple size={14} /> {busy ? "Menyimpan…" : isSubmitted ? "Simpan koreksi" : "Kirim laporan"}
                    </button>
                </div>
            </div>
        );
    }
    const generalErrors = errors.filter((e) => !e.field || e.field === "attachments");
    return (
        <div className="space-y-5 max-w-3xl" data-testid="simple-form">
            {header}
            {report && <StatusBanner report={report} />}
            <FormBody figures={figures} setField={setField} fieldErrors={fieldErrors} handover={handover} setHandover={setHandover} />
            <section className="bg-white/70 border border-mist rounded-sm p-4 md:p-5 space-y-5">
                <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Bukti (opsional · gambar atau PDF)</h3>
                <ProofUploader brand={brand} date={date} kind="jubelio_summary" title="Ringkasan penjualan / laporan transaksi Jubelio" hint="Disarankan untuk penjualan Ready Stock" items={jubelio} onChange={setJubelio} />
                <ProofUploader brand={brand} date={date} kind="other" title="Bukti lain" hint="Bukti transfer, DP / pelunasan PO, marketplace, ongkir" items={other} onChange={setOther} />
            </section>
            {generalErrors.length > 0 && (
                <ul className="border border-red-200 bg-red-50/60 rounded-sm p-3 text-xs text-red-800 space-y-1" data-testid="simple-errors">
                    {generalErrors.map((e, i) => <li key={i}>{e.message}</li>)}
                </ul>
            )}
            <div className="flex flex-wrap justify-end gap-3">
                {!isSubmitted && (
                    <button type="button" onClick={saveDraft} disabled={busy} className={btnGhost} data-testid="simple-draft-btn"><FloppyDisk size={14} /> Simpan draf</button>
                )}
                <button type="button" onClick={review} disabled={busy} className={btnPrimary} data-testid="simple-review-btn">
                    <Eye size={14} /> {busy ? "Memeriksa…" : isSubmitted ? "Periksa perubahan" : "Periksa & kirim"}
                </button>
            </div>
        </div>
    );
}
